'use client';

import { useState } from 'react';
import { Modal } from '@shared/ui';
import type { BaseModalProps } from '@shared/types/modal.types';
import { reviewsApi } from '@/src/shared/api/reviews.api';
import { usersApi, type ReportReason } from '@/src/shared/api/users.api';
import { toast } from 'sonner';

interface ReportModalProps extends BaseModalProps {
  userId?: string;
  userName?: string;
  reportType?: 'user' | 'task' | 'review';
  /** Target id for the reported entity. Required when reportType is 'review'. */
  targetId?: string;
}

// Maps UI reasons to the backend ReportReason enum.
const REPORT_REASONS = [
  { id: 'SPAM', label: 'Spam or misleading' },
  { id: 'INAPPROPRIATE', label: 'Inappropriate content' },
  { id: 'OFFENSIVE', label: 'Harassment or bullying' },
  { id: 'FAKE', label: 'Scam or fraud' },
  { id: 'OTHER', label: 'Other' },
];

/**
 * Report Modal - allows users to report content or users
 * Usage: const { open } = useModal('report'); open({ userId: 123, userName: 'John' });
 */
export function ReportModal({
  isOpen,
  onClose,
  userId,
  userName,
  reportType = 'user',
  targetId,
}: ReportModalProps) {
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reviews and user profiles have backend endpoints; task reports do not yet.
  const isReviewReport = reportType === 'review' && !!targetId;
  const isUserReport = reportType === 'user' && !!userId;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedReason) return;

    setError(null);

    if (!isReviewReport && !isUserReport) {
      setError('Reporting this content is not available yet.');
      return;
    }

    setIsSubmitting(true);

    try {
      if (isUserReport) {
        await usersApi.report(
          userId!,
          selectedReason as ReportReason,
          description.trim() || undefined
        );
      } else {
        await reviewsApi.report(targetId!, selectedReason, description.trim() || undefined);
      }

      toast.success('Thank you. Your report has been sent to moderation.');

      // Close modal and reset form
      onClose();
      setSelectedReason('');
      setDescription('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit report');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      animation="slide-up"
      title="Report"
      description={userName ? `Report ${userName}` : 'Report this content'}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Reason selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Reason for reporting
          </label>
          <div className="space-y-2">
            {REPORT_REASONS.map((reason) => (
              <label
                key={reason.id}
                className="flex items-center p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
              >
                <input
                  type="radio"
                  name="reason"
                  value={reason.id}
                  checked={selectedReason === reason.id}
                  onChange={(e) => setSelectedReason(e.target.value)}
                  className="mr-3"
                />
                <span className="text-sm text-gray-700">{reason.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Description */}
        <div>
          <label
            htmlFor="description"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Additional details (optional)
          </label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Please provide any additional information..."
          />
        </div>

        {error && (
          <p className="text-sm text-red-600" role="alert">
            {error}
          </p>
        )}

        {/* Actions */}
        <div className="flex gap-3 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!selectedReason || isSubmitting}
            className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Report'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
